import { FrondEngine, ToolKind, EraserMode } from "@frond/engine";
import { useEffect, useState } from "react";
import { loadActivePalette, saveActivePalette, TOOLS, ToolDef } from "../state/tools";

interface Props {
  engine: FrondEngine;
  version: number;
  open?: boolean;
  onToggle?: (open: boolean) => void;
}

export function ToolPanel({ engine, version, open, onToggle }: Props) {
  const tools = engine.tools;
  const [internalOpen, setInternalOpen] = useState(true);
  const isOpen = open !== undefined ? open : internalOpen;

  const setOpen = (val: boolean) => {
    if (onToggle) onToggle(val);
    setInternalOpen(val);
  };

  const [, setTick] = useState(0);
  useEffect(() => {
    const bump = () => setTick((t) => t + 1);
    const offs = [
      tools.on("tool", bump),
      tools.on("style", bump),
      tools.on("history", bump),
    ];
    return () => offs.forEach((off) => off());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [engine]);

  const groups: Array<{ name: string; tools: ToolDef[] }> = [
    { name: "Draw", tools: TOOLS.filter((t) => t.group === "draw") },
    { name: "Edit", tools: TOOLS.filter((t) => t.group === "edit") },
    { name: "Insert", tools: TOOLS.filter((t) => t.group === "insert") },
    { name: "Navigate", tools: TOOLS.filter((t) => t.group === "nav") },
  ];

  const drawTool =
    tools.tool === "pen" ||
    tools.tool === "pencil" ||
    tools.tool === "marker" ||
    tools.tool === "highlighter" ||
    tools.tool === "eraser" ||
    tools.tool === "fill" ||
    tools.tool === "text";

  const activeDef = TOOLS.find((t) => t.kind === tools.tool);

  if (!isOpen) {
    return (
      <button
        className="toolpanel-toggle"
        onClick={() => setOpen(true)}
        title="Open tools palette"
        aria-label="Open tools palette"
      >
        <span className="toolpanel-toggle-icon">{activeDef?.icon ?? "🛠️"}</span>
        <span className="toolpanel-toggle-dot" style={{ background: tools.style.color }} />
      </button>
    );
  }

  return (
    <>
      <div className="toolpanel-backdrop" onClick={() => setOpen(false)} />
      <aside className="toolpanel">
        <div className="toolpanel-head">
          <span className="toolpanel-title">Tools</span>
          <button
            className="toolpanel-collapse-btn"
            onClick={() => setOpen(false)}
            title="Collapse palette"
            aria-label="Collapse tools palette"
          >
            ◀
          </button>
        </div>
        <div className="tool-grid">
        {groups.map((g) => (
          <div className="tool-group" key={g.name}>
            <div className="tool-group-label">{g.name}</div>
            <div className="tool-row">
              {g.tools.map((t) => (
                <button
                  key={t.kind}
                  className={`tool-btn ${tools.tool === t.kind ? "active" : ""}`}
                  title={`${t.label}${t.key ? ` (${t.key})` : ""}`}
                  onClick={() => tools.setTool(t.kind)}
                >
                  <span className="tool-icon">{t.icon}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </div>

      {drawTool && <BrushSettings engine={engine} />}

      <div className="tool-foot">
        <button
          className="tool-btn"
          disabled={!engine.history.canUndo}
          onClick={() => engine.history.undo()}
          title="Undo (Ctrl+Z)"
        >
          ↩
        </button>
        <button
          className="tool-btn"
          disabled={!engine.history.canRedo}
          onClick={() => engine.history.redo()}
          title="Redo (Ctrl+Shift+Z)"
        >
          ↪
        </button>
        <button
          className="tool-btn"
          onClick={() => engine.fitView()}
          title="Fit view (0)"
        >
          ⛶
        </button>
        <button
          className="tool-btn"
          onClick={() => {
            engine.camera.rotation = 0;
            engine.requestRender();
          }}
          title="Reset rotation"
        >
          ⟲
        </button>
      </div>
    </aside>
    </>
  );
}

function BrushSettings({ engine }: { engine: FrondEngine }) {
  const tools = engine.tools;
  const [palette, setPalette] = useState<string[]>(loadActivePalette());
  const isEraser = tools.tool === "eraser";
  const isText = tools.tool === "text";
  const isFill = tools.tool === "fill";

  const pick = (color: string) => {
    tools.setColor(color);
    setPalette((prev) => {
      const next = [color, ...prev.filter((c) => c !== color)].slice(0, 12);
      saveActivePalette(next);
      return next;
    });
  };

  return (
    <div className="brush-settings">
      <div className="color-row">
        {palette.slice(0, 8).map((c) => (
          <button
            key={c}
            className={`swatch ${tools.style.color.toLowerCase() === c.toLowerCase() ? "active" : ""}`}
            style={{ background: c }}
            onClick={() => pick(c)}
            title={c}
          />
        ))}
        <label className="swatch custom" title="Custom color">
          <input
            type="color"
            value={tools.style.color}
            onChange={(e) => pick(e.target.value)}
          />
          <span className="rainbow">＋</span>
        </label>
      </div>

      {!isText && !isFill && (
        <label className="slider-row">
          <span>Size</span>
          <input
            type="range"
            min={isEraser ? 2 : 0.5}
            max={isEraser ? 120 : 60}
            step={0.5}
            value={tools.style.size}
            onChange={(e) => tools.setSize(Number(e.target.value))}
          />
          <span className="slider-val">{tools.style.size.toFixed(1)}</span>
        </label>
      )}

      {!isEraser && !isText && (
        <label className="slider-row">
          <span>Opacity</span>
          <input
            type="range"
            min={0.05}
            max={1}
            step={0.05}
            value={tools.style.opacity}
            onChange={(e) => tools.setOpacity(Number(e.target.value))}
          />
          <span className="slider-val">{Math.round(tools.style.opacity * 100)}%</span>
        </label>
      )}

      {isEraser && (
        <div className="eraser-modes" title="Erase relative to the current zoom level">
          {(
            [
              ["above", "🪐", "Erase bigger content (above current zoom)"],
              ["current", "🧍", "Erase at the current zoom level"],
              ["below", "🦠", "Erase smaller content (below current zoom)"],
            ] as Array<[EraserMode, string, string]>
          ).map(([mode, icon, tip]) => (
            <button
              key={mode}
              className={`tool-btn ${tools.style.eraserMode === mode ? "active" : ""}`}
              title={tip}
              onClick={() => tools.setEraserMode(mode)}
            >
              {icon}
            </button>
          ))}
        </div>
      )}

      {!isText && (
        <label className="check-row">
          <input
            type="checkbox"
            checked={tools.style.fingerDraw}
            onChange={(e) => tools.setFingerDraw(e.target.checked)}
          />
          <span>Draw with finger</span>
        </label>
      )}

      <label className="check-row">
        <input
          type="checkbox"
          checked={engine.grid.enabled}
          onChange={(e) => {
            engine.setGrid({ enabled: e.target.checked });
          }}
        />
        <span>Show grid</span>
      </label>
    </div>
  );
}
